import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  alpha,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import IconButton from '@mui/material/IconButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';

import { format } from 'date-fns';
import LaunchConfirmDialog from './LaunchConfirmDialog';
import ImportResult from './ImportResult';
import OutboundReadiness from './OutboundReadiness';
import { campaignApi, ImportReport } from '../services/campaignApi';
import AgentSelect from './AgentSelect';
import PhoneNumberSelect, { PhoneChannel, fetchPhoneChannels } from './PhoneNumberSelect';
import TimeInput from './TimeInput';
import { compileTemplate, extractVariables } from '../utils/templateCompiler';

interface CreateCampaignProps {
  onBack: () => void;
  onSuccess?: () => void;
  campaignId?: string | null;
}

const steps = ['Campaign Details', 'Upload Contacts', 'Call Configuration'];

const CreateCampaign: React.FC<CreateCampaignProps> = ({ onBack, onSuccess, campaignId: initialCampaignId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [campaignId, setCampaignId] = useState<string | null>(initialCampaignId || null);
  const [isEditMode, setIsEditMode] = useState(!!initialCampaignId);

  // Step 0: Details
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [agentId, setAgentId] = useState<number | null>(null);
  const [fromNumber, setFromNumber] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Step 1: Upload
  const [csvFile, setCsvFile] = useState<File | null>(null);
  // What the last upload actually did. Null until one has happened.
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const [listName, setListName] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Contact Lists State (for editing)
  const [contactLists, setContactLists] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [isCampaignLaunched, setIsCampaignLaunched] = useState(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});

  // API State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  

  // Launch Settings (Step 2)
  const [useCustomHours, setUseCustomHours] = useState(false);
  const [useRetryConfig, setUseRetryConfig] = useState(false);
  const [scheduleTime, setScheduleTime] = useState<Date | null>(null);
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false
  });
  const [dayTimes, setDayTimes] = useState<Record<string, { start: string; end: string }>>({
    monday: { start: '09:00:00', end: '17:00:00' },
    tuesday: { start: '09:00:00', end: '17:00:00' },
    wednesday: { start: '09:00:00', end: '17:00:00' },
    thursday: { start: '09:00:00', end: '17:00:00' },
    friday: { start: '09:00:00', end: '17:00:00' },
    saturday: { start: '09:00:00', end: '17:00:00' },
    sunday: { start: '09:00:00', end: '17:00:00' }
  });
  const [specialClosures, setSpecialClosures] = useState<Record<string, { start_time: string; end_time: string }>>({});
  const [closureDate, setClosureDate] = useState<Date | null>(null);
  const [closureStartTime, setClosureStartTime] = useState<Date | null>(null);
  const [closureEndTime, setClosureEndTime] = useState<Date | null>(null);
  const [retryConfig, setRetryConfig] = useState({ enabled: false, max_retries: 3, retry_time_limit_minutes: 60 });
  const [useVoicemailHandling, setUseVoicemailHandling] = useState(false);
  const [voicemailOption, setVoicemailOption] = useState<{ type: 'static_text' | 'hangup'; text?: string }>({ type: 'static_text', text: '' });
  const [campaignGreeting, setCampaignGreeting] = useState('');
  const [campaignInstructions, setCampaignInstructions] = useState('');

  // State to control date picker open/close
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [scheduleDatePickerOpen, setScheduleDatePickerOpen] = useState(false);

  // Channels are prefetched when navigating to campaigns page, no need to fetch again

  // Extract all unique prompt variable keys from contacts (including custom_fields)
  // Only show variables that actually exist in the data
  // phone_number is always included since it's required for CSV uploads
  const availablePromptVariables = useMemo(() => {
    // Collect all unique keys from contacts' prompt_variables and custom_fields
    const allKeys = new Set<string>();
    
    // Track which standard fields exist in contacts
    let hasFirstName = false;
    let hasLastName = false;
    let hasEmail = false;
    
    contacts.forEach((contact: any) => {
      // Extract from prompt_variables
      if (contact.prompt_variables && typeof contact.prompt_variables === 'object') {
        Object.keys(contact.prompt_variables).forEach(key => {
          allKeys.add(key);
        });
      }
      // Extract from custom_fields
      if (contact.custom_fields && typeof contact.custom_fields === 'object') {
        Object.keys(contact.custom_fields).forEach(key => {
          allKeys.add(key);
        });
      }
      // Check standard fields if they exist (non-null, non-empty)
      if (contact.first_name) hasFirstName = true;
      if (contact.last_name) hasLastName = true;
      if (contact.email) hasEmail = true;
    });
    
    // Always include phone_number since it's required for CSV uploads
    allKeys.add('phone_number');
    
    // Only include standard fields if they actually exist in at least one contact
    if (hasFirstName) allKeys.add('first_name');
    if (hasLastName) allKeys.add('last_name');
    if (hasEmail) allKeys.add('email');

    // Convert to variable format - only show what actually exists
    return Array.from(allKeys).map(key => ({
      display: key === 'phone_number' ? 'Phone Number' : 
               key === 'first_name' ? 'First Name' :
               key === 'last_name' ? 'Last Name' :
               key === 'caller_name' ? 'Caller Name' :
               key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      value: `{${key}}`
    }));
  }, [contacts]);

  // Load campaign data if editing or if campaign was created during flow
  React.useEffect(() => {
    if (campaignId) {
      setIsLoading(true);
      setError(null);
      // Use getCampaigns and find the campaign, or create a getCampaign method
      Promise.all([
        campaignApi.getCampaigns(),
        campaignApi.getContactLists(campaignId),
        campaignApi.getCampaignContacts(campaignId).catch(() => [])
      ])
        .then(([campaigns, lists, contactsData]) => {
          const campaign = campaigns.find((c: any) => c.id.toString() === campaignId.toString());
          if (campaign) {
            // Check if campaign is launched
            const launched = campaign.isLaunched || false;
            setIsCampaignLaunched(launched);
            if (launched) {
              setError('This campaign has been launched and cannot be edited');
              return;
            }
            
            setCampaignName(campaign.name || '');
            setCampaignDescription(campaign.description || '');
            setAgentId(campaign.agent_id || null);
            setFromNumber(campaign.from_number || '');
            if (campaign.start_date) {
              setStartDate(new Date(campaign.start_date));
            }
            if (campaign.end_date) {
              setEndDate(new Date(campaign.end_date));
            }
            
            // Load launch_settings if present
            if (campaign.launch_settings) {
              const settings = campaign.launch_settings;
              
              // Load voicemail_option
              if (settings.voicemail_option) {
                setUseVoicemailHandling(true);
                setVoicemailOption({
                  type: settings.voicemail_option.action.type,
                  text: settings.voicemail_option.action.text || ''
                });
              }
              
              // Load calling_hours
              if (settings.calling_hours) {
                setUseCustomHours(true);
                setTimezone(settings.calling_hours.timezone || 'America/Los_Angeles');
                if (settings.calling_hours.weekly_hours) {
                  const weeklyHours = settings.calling_hours.weekly_hours;
                  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                  const newEnabledDays: Record<string, boolean> = {};
                  const newDayTimes: Record<string, { start: string; end: string }> = {};
                  
                  daysOfWeek.forEach(day => {
                    if (weeklyHours[day]) {
                      newEnabledDays[day] = true;
                      newDayTimes[day] = {
                        start: weeklyHours[day].start_time || weeklyHours[day].start || '09:00:00',
                        end: weeklyHours[day].end_time || weeklyHours[day].end || '17:00:00'
                      };
                    } else {
                      newEnabledDays[day] = false;
                      newDayTimes[day] = { start: '09:00:00', end: '17:00:00' };
                    }
                  });
                  
                  setEnabledDays(newEnabledDays);
                  setDayTimes(newDayTimes);
                }
                if (settings.calling_hours.special_closures) {
                  setSpecialClosures(settings.calling_hours.special_closures);
                }
              }
              
              // Load retry_config
              if (settings.retry_config) {
                setUseRetryConfig(true);
                setRetryConfig({
                  enabled: true,
                  max_retries: settings.retry_config.max_retries || 3,
                  retry_time_limit_minutes: settings.retry_config.retry_time_limit_minutes || 60
                });
              }
              
              // Load schedule_dt
              if (settings.schedule_dt) {
                setScheduleTime(new Date(settings.schedule_dt));
              }
              
              // Load campaign greeting and instructions templates
              if (settings.greeting_template) {
                setCampaignGreeting(settings.greeting_template);
              }
              if (settings.instructions_template) {
                setCampaignInstructions(settings.instructions_template);
              }
            }
          } else {
            setError('Campaign not found');
          }
          setContactLists(lists || []);
          setContacts(contactsData || []);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load campaign');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isEditMode, campaignId]);

  // Helper function to parse YYYY-MM-DD date string as local date (avoid timezone issues)
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  // Closure handlers
  const handleAddClosure = () => {
    if (closureDate && closureStartTime && closureEndTime) {
      const dateString = format(closureDate, 'yyyy-MM-dd');
      setSpecialClosures(prev => ({
        ...prev,
        [dateString]: {
          start_time: formatTimeToString(closureStartTime),
          end_time: formatTimeToString(closureEndTime),
        },
      }));
      setClosureDate(null);
      setClosureStartTime(null);
      setClosureEndTime(null);
    }
  };

  const handleRemoveClosure = (date: string) => {
    setSpecialClosures(prev => {
      const newClosures = { ...prev };
      delete newClosures[date];
      return newClosures;
    });
  };

  const handleCreateCampaign = async () => {
    if (!isStep0Valid) return;
    setIsLoading(true);
    setError(null);
    try {
      // Build launch_settings from current form state (only schedule, calling hours, retry config)
      // Voicemail, greeting, and instructions are saved in step 2
      const launchSettings: any = {};
      
      // Add calling_hours if custom hours are enabled
      if (useCustomHours) {
        launchSettings.calling_hours = {
          timezone: timezone,
          weekly_hours: generateWeeklyHours(),
          special_closures: specialClosures
        };
      }
      
      // Add retry_config if enabled
      if (useRetryConfig) {
        launchSettings.retry_config = {
          max_retries: retryConfig.max_retries,
          retry_time_limit_minutes: retryConfig.retry_time_limit_minutes
        };
      }
      
      // Add schedule_dt if provided
      if (scheduleTime) {
        launchSettings.schedule_dt = scheduleTime.toISOString();
      }
      
      const campaignData: any = {
        name: campaignName,
        description: campaignDescription,
        agent_id: agentId,
        from_number: fromNumber,
        start_date: startDate?.toISOString(),
        end_date: endDate?.toISOString(),
      };
      
      // Only include launch_settings if there are any settings
      if (Object.keys(launchSettings).length > 0) {
        campaignData.launch_settings = launchSettings;
      }
      
      if (campaignId) {
        // Update existing campaign (either in edit mode or if campaign was already created)
        await campaignApi.updateCampaign(campaignId, campaignData);
        if (isEditMode && onSuccess) {
          onSuccess();
        } else {
          // In create mode, advance to next step after updating
          setActiveStep(1);
        }
      } else {
        // Create new campaign
        const createdCampaign = await campaignApi.createCampaign(campaignData);
        setCampaignId(createdCampaign.id);
        setActiveStep(1);
      }
    } catch (err: any) {
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} campaign`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCallConfiguration = async () => {
    if (!campaignId) return;
    setIsLoading(true);
    setError(null);
    try {
      // Get existing campaign to preserve existing launch_settings
      const campaigns = await campaignApi.getCampaigns();
      const existingCampaign = campaigns.find((c: any) => c.id.toString() === campaignId.toString());
      const existingLaunchSettings = existingCampaign?.launch_settings || {};
      
      // Build launch_settings with voicemail, greeting, and instructions
      const launchSettings: any = {
        ...existingLaunchSettings, // Preserve existing settings (calling_hours, retry_config, schedule_dt)
      };
      
      // Add voicemail_option if enabled - send template as-is (with {var} placeholders)
      // Backend will convert to {{var}} format and handle dynamic variables
      if (useVoicemailHandling) {
        launchSettings.voicemail_option = {
          action: {
            type: voicemailOption.type,
            ...(voicemailOption.type === 'static_text' && voicemailOption.text ? { text: voicemailOption.text } : {})
          }
        };
      } else {
        // Remove voicemail_option if disabled
        delete launchSettings.voicemail_option;
      }
      
      // Add campaign greeting and instructions templates (will be compiled per contact at launch time)
      if (campaignGreeting) {
        launchSettings.greeting_template = campaignGreeting;
      } else {
        delete launchSettings.greeting_template;
      }
      
      if (campaignInstructions) {
        launchSettings.instructions_template = campaignInstructions;
      } else {
        delete launchSettings.instructions_template;
      }
      
      const updateData: any = {
        launch_settings: launchSettings
      };
      
      await campaignApi.updateCampaign(campaignId, updateData);
      
      // Show success message
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save call configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadList = async () => {
    if (!csvFile || !campaignId) return;
    setIsLoading(true);
    setError(null);
    try {
      const name = listName.trim() || csvFile.name;
      // The response says how many of the rows were actually imported. The
      // old code awaited it and threw it away, which is why a file that
      // imported none of its 31 patients still looked like a success.
      const uploaded = await campaignApi.uploadContactList(campaignId, csvFile, name);
      setImportReport(uploaded?.import ?? null);
      // Reload lists and contacts after upload (for both edit and create modes)
      const [lists, contactsData] = await Promise.all([
        campaignApi.getContactLists(campaignId),
        campaignApi.getCampaignContacts(campaignId).catch(() => [])
      ]);
      setContactLists(lists || []);
      setContacts(contactsData || []);
      setCsvFile(null);
      setListName('');
      // Stay on step 1 (upload page) so user can review contacts and upload more lists
      // User will manually advance to launch step when ready
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to upload contact list';
      setError(errorMessage);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteList = async (listId: string) => {
    if (!campaignId || !window.confirm('Are you sure you want to delete this contact list?')) return;
    setIsLoading(true);
    setError(null);
    try {
      await campaignApi.deleteContactList(campaignId, listId);
      const lists = await campaignApi.getContactLists(campaignId);
      setContactLists(lists || []);
      // Remove contacts that belong to the deleted list
      setContacts(prevContacts => 
        prevContacts.filter(contact => contact.contact_list_id?.toString() !== listId)
      );
    } catch (err: any) {
      setError(err.message || 'Failed to delete contact list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv')) {
        setCsvFile(file);
        setListName('');
        setImportReport(null);
      } else {
        setError('Please drop a CSV file');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.name.endsWith('.csv')) {
        setCsvFile(file);
        setListName('');
        setImportReport(null);
      } else {
        setError('Please select a CSV file');
      }
    }
  };

  // Helper functions for launch settings
  const parseTimeString = (timeStr: string): Date => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds || 0);
    return date;
  };

  const formatTimeToString = (time: Date | null): string => {
    if (!time) return '00:00:00';
    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    const seconds = time.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const generateWeeklyHours = () => {
    const weeklyHours: Record<string, { start_time: string; end_time: string }> = {};
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    daysOfWeek.forEach(day => {
      if (enabledDays[day]) {
        weeklyHours[day] = {
          start_time: dayTimes[day]?.start || '09:00:00',
          end_time: dayTimes[day]?.end || '17:00:00'
        };
      }
    });
    return weeklyHours;
  };

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Called once the calls are actually scheduled.
  const handleLaunched = (scheduledCalls: any[]) => {
    setConfirmOpen(false);
    const errors = (scheduledCalls || [])
      .filter((call: any) => call.error)
      .map((call: any) => `${call.to_number}: ${call.error}`)
      .join('\n');
    if (errors) {
      setError(`Some calls failed to schedule:\n${errors}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (onSuccess) onSuccess();
  };

  const handleLaunchCampaign = async () => {
    if (!campaignId) return;
    setIsLoading(true);
    setError(null);
    try {
      // Get existing campaign to preserve any existing launch_settings
      const campaigns = await campaignApi.getCampaigns();
      const existingCampaign = campaigns.find((c: any) => c.id.toString() === campaignId.toString());
      const existingLaunchSettings = existingCampaign?.launch_settings || {};
      
      // Build launch_settings from current form state, preserving existing settings
      const launchSettings: any = {
        ...existingLaunchSettings, // Preserve any settings not in the form
      };
      
      // Add calling_hours if custom hours are enabled
      if (useCustomHours) {
        launchSettings.calling_hours = {
          timezone: timezone,
          weekly_hours: generateWeeklyHours(),
          special_closures: specialClosures
        };
      } else {
        delete launchSettings.calling_hours;
      }
      
      // Add retry_config if enabled
      if (useRetryConfig) {
        launchSettings.retry_config = {
          max_retries: retryConfig.max_retries,
          retry_time_limit_minutes: retryConfig.retry_time_limit_minutes
        };
      } else {
        delete launchSettings.retry_config;
      }
      
      // Add schedule_dt if provided
      if (scheduleTime) {
        launchSettings.schedule_dt = scheduleTime.toISOString();
      } else {
        delete launchSettings.schedule_dt;
      }
      
      // Add voicemail_option if enabled - send template as-is (with {var} placeholders)
      // Backend will convert to {{var}} format and handle dynamic variables
      if (useVoicemailHandling) {
        launchSettings.voicemail_option = {
          action: {
            type: voicemailOption.type,
            ...(voicemailOption.type === 'static_text' && voicemailOption.text ? { text: voicemailOption.text } : {})
          }
        };
      } else {
        delete launchSettings.voicemail_option;
      }
      
      // Add campaign greeting and instructions templates (will be compiled per contact at launch time)
      if (campaignGreeting) {
        launchSettings.greeting_template = campaignGreeting;
      } else {
        delete launchSettings.greeting_template;
      }
      
      if (campaignInstructions) {
        launchSettings.instructions_template = campaignInstructions;
      } else {
        delete launchSettings.instructions_template;
      }
      
      // Update campaign with launch_settings before launching
      const updateData: any = {
        launch_settings: launchSettings
      };
      
        await campaignApi.updateCampaign(campaignId, updateData);
      
      // Settings are saved; the dialog does the dialling. It shows how many
      // people would be called and requires that number to be typed back,
      // because launching places real calls and there is no undo.
      setConfirmOpen(true);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to launch campaign';
      setError(errorMessage);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleEditContact = (contact: any) => {
    setEditingContact(contact);
    setEditFormData({
      phone_number: contact.phone_number || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      custom_fields: contact.custom_fields ? { ...contact.custom_fields } : {},
    });
  };

  const handleSaveContact = async () => {
    if (!campaignId || !editingContact) return;
    setIsLoading(true);
    setError(null);
    try {
      const updated = await campaignApi.updateContact(campaignId, editingContact.id.toString(), editFormData);
      setContacts(contacts.map(c => c.id === updated.id ? updated : c));
      setEditingContact(null);
      setEditFormData({});
    } catch (err: any) {
      setError(err.message || 'Failed to update contact');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!campaignId || !window.confirm('Are you sure you want to delete this contact?')) return;
    setIsLoading(true);
    setError(null);
    try {
      await campaignApi.deleteContact(campaignId, contactId.toString());
      setContacts(contacts.filter(c => c.id !== contactId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete contact');
    } finally {
      setIsLoading(false);
    }
  };

  const isStep0Valid = campaignName.trim().length > 0 && agentId !== null && fromNumber !== '' && startDate !== null && endDate !== null && startDate < endDate;
  const isStep1Valid = csvFile !== null;

  // Helper function to get the reason why step 0 is invalid
  const getStep0ValidationMessage = (): string | null => {
    if (campaignName.trim().length === 0) {
      return 'Campaign name is required';
    }
    if (agentId === null) {
      return 'Please select an agent';
    }
    if (fromNumber === '') {
      return 'Please select a phone number';
    }
    if (startDate === null) {
      return 'Start date is required';
    }
    if (endDate === null) {
      return 'End date is required';
    }
    if (startDate >= endDate) {
      // Check if dates are the same day
      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      if (startDateOnly.getTime() === endDateOnly.getTime()) {
        return 'Start date and end date must be different dates';
      }
      return 'End date must be after start date';
    }
    return null;
  };

  const step0ValidationMessage = getStep0ValidationMessage();

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: alpha('#000', 0.6) }}>
          Back to Campaigns
        </Button>
      </Box>

      <Typography variant="h2" sx={{ mb: 4, fontWeight: 600 }}>
        {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
      </Typography>

      {/* Read before anything is built. The alternative is what used to
          happen: upload a list, configure the calls, press launch, and only
          then learn the agent cannot place outbound calls. */}
      <OutboundReadiness />

      {!isEditMode && (
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Paper sx={{ p: 4, minHeight: '400px' }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              position: 'sticky',
              top: 16,
              zIndex: 1000,
              boxShadow: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            onClose={() => setError(null)}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Error</Typography>
            <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</Typography>
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>

            {activeStep === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Campaign Details & Configuration</Typography>
                {isEditMode && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Note: Once a campaign is launched, it cannot be edited. Make sure all details are correct before launching.
                  </Alert>
                )}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                      <TextField
                        label="Campaign Name"
                        fullWidth
                        required
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        placeholder="e.g., Summer Sale Outreach"
                        disabled={isEditMode && isLoading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                            <Tooltip title="A descriptive name for your campaign (e.g., 'Summer Sale Outreach')" arrow>
                              <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                            </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                  </Grid>
                  <Grid item xs={12}>
                      <TextField
                        label="Description / Notes"
                        fullWidth
                        multiline
                        rows={4}
                        value={campaignDescription}
                        onChange={(e) => setCampaignDescription(e.target.value)}
                        placeholder="Add any notes or details about this campaign..."
                        disabled={isEditMode && isLoading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                            <Tooltip title="Optional notes or details about this campaign" arrow>
                              <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                            </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative' }}>
                        <AgentSelect value={agentId} onChange={(id) => setAgentId(id)} disabled={isEditMode && isLoading} />
                      <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <Tooltip title="Select the AI agent that will handle calls for this campaign" arrow>
                          <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18, pointerEvents: 'auto' }} />
                    </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ position: 'relative' }}>
                        <PhoneNumberSelect
                          value={fromNumber || ''}
                          onChange={(value) => setFromNumber(value)}
                          label="From Number"
                          showAllOption={false}
                          disabled={isEditMode && isLoading}
                          includeVerifiedCallerIds={true}
                        />
                      <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <Tooltip title="The phone number that will appear as the caller ID for outbound calls" arrow>
                          <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18, pointerEvents: 'auto' }} />
                    </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <Box sx={{ width: '100%' }}>
                        <DatePicker
                          label="Start Date"
                          value={startDate}
                          onChange={(date) => setStartDate(date)}
                          open={startDatePickerOpen}
                          onOpen={() => setStartDatePickerOpen(true)}
                          onClose={() => setStartDatePickerOpen(false)}
                          disabled={isEditMode && isLoading}
                          slots={{
                            openPickerIcon: CalendarTodayIcon,
                          }}
                          slotProps={{
                            textField: {
                              required: true,
                              fullWidth: true,
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: alpha('#000', 0.02),
                                },
                              },
                              InputProps: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => setStartDatePickerOpen(true)}
                                        sx={{ p: 0.5 }}
                                      >
                                        <CalendarTodayIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                      </IconButton>
                                    <Tooltip title="Calls from this campaign will not be placed before this date." arrow>
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                    </Tooltip>
                                    </Stack>
                                  </InputAdornment>
                                ),
                              },
                            },
                          }}
                        />
                      </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <Box sx={{ width: '100%' }}>
                        <DatePicker
                          label="End Date"
                          value={endDate}
                          onChange={(date) => setEndDate(date)}
                          open={endDatePickerOpen}
                          onOpen={() => setEndDatePickerOpen(true)}
                          onClose={() => setEndDatePickerOpen(false)}
                          disabled={isEditMode && isLoading}
                          slots={{
                            openPickerIcon: CalendarTodayIcon,
                          }}
                          slotProps={{
                            textField: {
                              required: true,
                              fullWidth: true,
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: alpha('#000', 0.02),
                                },
                              },
                              InputProps: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => setEndDatePickerOpen(true)}
                                        sx={{ p: 0.5 }}
                                      >
                                        <CalendarTodayIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                      </IconButton>
                                    <Tooltip title="Calls from this campaign will not be placed after this date." arrow>
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                    </Tooltip>
                                    </Stack>
                                  </InputAdornment>
                                ),
                              },
                            },
                          }}
                        />
                      </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: alpha('#000', 0.6), fontStyle: 'italic' }}>
                      Note: Start date and end date must be different dates. The end date must be after the start date.
                    </Typography>
                  </Grid>
                  
                  {/* Voicemail Handling, Greeting, and Instructions - shown in edit mode */}
                  {isEditMode && (
                    <>
                  {/* Voicemail Handling Toggle */}
                  <Grid item xs={12}>
                    <Tooltip title="Enable voicemail detection and handling. When enabled, you can configure the agent to leave a static message or hang up when voicemail is detected." arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useVoicemailHandling}
                            onChange={(e) => setUseVoicemailHandling(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Voicemail Handling
                            <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 16 }} />
                          </Box>
                        }
                      />
                    </Tooltip>
                  </Grid>

                  {/* Voicemail Handling Section */}
                  <Grid item xs={12}>
                    <Collapse in={useVoicemailHandling}>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                                <Box sx={{ position: 'relative' }}>
                              <FormControl fullWidth>
                                <InputLabel>Voicemail Action</InputLabel>
                                <Select
                                  value={voicemailOption.type}
                                  label="Voicemail Action"
                                  onChange={(e) => setVoicemailOption(prev => ({
                                    ...prev,
                                    type: e.target.value as 'static_text' | 'hangup'
                                  }))}
                                >
                                  <MenuItem value="static_text">Leave Static Message</MenuItem>
                                  <MenuItem value="hangup">Hang Up</MenuItem>
                                </Select>
                              </FormControl>
                                  <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                    <Tooltip title="How the agent should handle voicemail detection. Static message supports template variables like {caller_name} that will be replaced with actual values. Hangup will immediately end the call." arrow>
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18, pointerEvents: 'auto' }} />
                            </Tooltip>
                                  </Box>
                                </Box>
                          </Grid>
                          {voicemailOption.type === 'static_text' && (
                            <>
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                                  Available Variables (click to insert):
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                  {availablePromptVariables.map((variable) => (
                                    <Chip
                                      key={variable.value}
                                      label={variable.display}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        const currentText = voicemailOption.text || '';
                                        setVoicemailOption(prev => ({
                                          ...prev,
                                          text: currentText + variable.value
                                        }));
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  ))}
                                </Stack>
                              </Grid>
                              <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Voicemail Message Template"
                                    value={voicemailOption.text || ''}
                                    onChange={(e) => setVoicemailOption(prev => ({
                                      ...prev,
                                      text: e.target.value
                                    }))}
                                    placeholder="Hi {caller_name}, please give us a callback tomorrow at 10am. You can reach us at {phone_number}."
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip 
                                              title="Template message with variables. Use {variableName} format. Variables will be replaced with actual values from each contact when the call is scheduled."
                                              arrow
                                            >
                                          <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                            </Tooltip>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Grid>

                  {/* Campaign Greeting and Instructions */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                      Campaign Greeting and Instructions
                    </Typography>
                    <Tooltip 
                      title="Set the greeting and instructions for all calls in this campaign. Templates will be compiled per contact using each contact's data when the campaign is launched."
                      arrow
                    >
                      <InfoIcon sx={{ ml: 1, fontSize: 18, color: alpha('#000', 0.4), verticalAlign: 'middle', mb: 1 }} />
                    </Tooltip>
                    
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                            Greeting Variables (click to insert):
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                            {availablePromptVariables.map((variable) => (
                              <Chip
                                key={variable.value}
                                label={variable.display}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const currentGreeting = campaignGreeting || '';
                                  setCampaignGreeting(currentGreeting + variable.value);
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Greeting"
                              value={campaignGreeting}
                              onChange={(e) => setCampaignGreeting(e.target.value)}
                              placeholder="Hi {caller_name}, this is a call from..."
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                      <Tooltip 
                                        title="The greeting for all campaign calls. Use {variableName} format for template variables. Will be compiled per contact using contact data when campaign is launched."
                                        arrow
                                      >
                                    <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                      </Tooltip>
                                  </InputAdornment>
                                ),
                              }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                            Instructions Variables (click to insert):
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                            {availablePromptVariables.map((variable) => (
                              <Chip
                                key={variable.value}
                                label={variable.display}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const currentInstructions = campaignInstructions || '';
                                  setCampaignInstructions(currentInstructions + variable.value);
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label="Campaign Instructions"
                              value={campaignInstructions}
                              onChange={(e) => setCampaignInstructions(e.target.value)}
                              placeholder="Remember to mention {appointment_date} when speaking with {caller_name}..."
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                      <Tooltip 
                                        title="Additional instructions for the agent for all campaign calls. Use {variableName} format for template variables. Will be compiled per contact using contact data when campaign is launched."
                                        arrow
                                      >
                                    <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                      </Tooltip>
                                  </InputAdornment>
                                ),
                              }}
                            />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                    </>
                  )}

                  {/* Schedule Time */}
                  <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={8}>
                            <DatePicker
                              label="Schedule Date"
                              value={scheduleTime}
                              onChange={(newDate) => {
                                if (newDate && !isNaN(newDate.getTime())) {
                                  const currentDate = scheduleTime || new Date();
                                  const updatedDate = new Date(newDate);
                                  updatedDate.setHours(currentDate.getHours());
                                  updatedDate.setMinutes(currentDate.getMinutes());
                                  setScheduleTime(updatedDate);
                                } else {
                                  setScheduleTime(null);
                                }
                              }}
                              open={scheduleDatePickerOpen}
                              onOpen={() => setScheduleDatePickerOpen(true)}
                              onClose={() => setScheduleDatePickerOpen(false)}
                              slots={{
                                openPickerIcon: CalendarTodayIcon,
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!(scheduleTime && isNaN(scheduleTime.getTime())),
                                  helperText: scheduleTime && isNaN(scheduleTime.getTime()) ? 'Invalid date' : '',
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02),
                                    },
                                  },
                                  InputProps: {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <IconButton
                                            size="small"
                                            onClick={() => setScheduleDatePickerOpen(true)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <CalendarTodayIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                          </IconButton>
                                        <Tooltip title="Leave blank to start calls as soon as possible. If set, the first call for this campaign will be scheduled at this time, and then following calls will be scheduled as soon as possible following." arrow>
                                          <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                        </Tooltip>
                                        </Stack>
                                      </InputAdornment>
                                    ),
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TimeInput
                              value={scheduleTime}
                              onChange={(newDate) => {
                                if (newDate && !isNaN(newDate.getTime())) {
                                  setScheduleTime(newDate);
                                } else {
                                  setScheduleTime(null);
                                }
                              }}
                              disabled={!scheduleTime}
                            />
                          </Grid>
                        </Grid>
                    </LocalizationProvider>
                  </Grid>

                  {/* Custom Hours Toggle */}
                  <Grid item xs={12}>
                    <Tooltip title="Enable custom calling hours to restrict when calls can be placed. These hours will override the calling hours set in the outbound agent configuration. When enabled, you can set specific hours for each day of the week and configure timezone settings." arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useCustomHours}
                            onChange={(e) => setUseCustomHours(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Use Custom Calling Hours
                            <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 16 }} />
                          </Box>
                        }
                      />
                    </Tooltip>
                  </Grid>

                  {/* Calling Hours Section */}
                  <Grid item xs={12}>
                    <Collapse in={useCustomHours}>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                              <TextField
                                select
                                fullWidth
                                label="Timezone"
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                    <Tooltip title="The timezone for your calling hours. All times will be interpreted in this timezone." arrow>
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                    </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                              >
                                <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                                <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                                <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                                <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                                <MenuItem value="America/Anchorage">Alaska Time (AKT)</MenuItem>
                                <MenuItem value="Pacific/Honolulu">Hawaii Time (HT)</MenuItem>
                              </TextField>
                          </Grid>
                          
                          {/* Day Times */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, mb: 1 }}>
                              <Typography variant="subtitle2">
                                Calling Hours by Day
                              </Typography>
                              <Tooltip title="Calls will take place between the start time and end time on each enabled day." arrow>
                                <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 16 }} />
                              </Tooltip>
                            </Box>
                            <Grid container spacing={2}>
                              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                                <Grid item xs={12} sm={6} key={day}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={enabledDays[day]}
                                        onChange={(e) => {
                                          setEnabledDays(prev => ({ ...prev, [day]: e.target.checked }));
                                        }}
                                      />
                                    }
                                    label={day.charAt(0).toUpperCase() + day.slice(1)}
                                  />
                                  {enabledDays[day] && (
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                      <TimePicker
                                        label="Start"
                                        value={parseTimeString(dayTimes[day]?.start || '09:00:00')}
                                        onChange={(newValue) => {
                                          if (newValue) {
                                            setDayTimes(prev => ({
                                              ...prev,
                                              [day]: { ...prev[day], start: formatTimeToString(newValue) }
                                            }));
                                          }
                                        }}
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            size: "small",
                                          }
                                        }}
                                      />
                                      <TimePicker
                                        label="End"
                                        value={parseTimeString(dayTimes[day]?.end || '17:00:00')}
                                        onChange={(newValue) => {
                                          if (newValue) {
                                            setDayTimes(prev => ({
                                              ...prev,
                                              [day]: { ...prev[day], end: formatTimeToString(newValue) }
                                            }));
                                          }
                                        }}
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            size: "small",
                                          }
                                        }}
                                      />
                                    </Stack>
                                  )}
                                </Grid>
                              ))}
                            </Grid>
                          </Grid>

                          {/* Special Closures */}
                          <Box sx={{ mt: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                Special Closures
                              </Typography>
                              <Tooltip title="Calls will be blocked during the special closures period." arrow>
                                <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 16 }} />
                              </Tooltip>
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <DatePicker
                                  label="Closure Date"
                                  value={closureDate}
                                  onChange={setClosureDate}
                                  slots={{
                                    openPickerIcon: CalendarTodayIcon,
                                  }}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      sx: {
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: alpha('#000', 0.02),
                                        },
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              {closureDate && (
                                <>
                                  <Grid item xs={12} sm={6}>
                                    <TimePicker
                                      label="Start Time"
                                      value={closureStartTime}
                                      onChange={setClosureStartTime}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          size: "small",
                                          sx: {
                                            backgroundColor: alpha('#000', 0.02),
                                            borderRadius: 2
                                          }
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TimePicker
                                      label="End Time"
                                      value={closureEndTime}
                                      onChange={setClosureEndTime}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          size: "small",
                                          sx: {
                                            backgroundColor: alpha('#000', 0.02),
                                            borderRadius: 2
                                          }
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Button
                                      variant="outlined"
                                      onClick={handleAddClosure}
                                      sx={{
                                        borderStyle: 'dashed',
                                        color: 'text.secondary'
                                      }}
                                    >
                                      Add Closure
                                    </Button>
                                  </Grid>
                                </>
                              )}
                            </Grid>

                            {/* Display existing closures */}
                            {Object.entries(specialClosures || {}).length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Existing Closures
                                </Typography>
                                <Stack spacing={1}>
                                  {Object.entries(specialClosures || {}).map(([date, times]: [string, any]) => (
                                    <Paper
                                      key={date}
                                      sx={{
                                        p: 2,
                                        backgroundColor: alpha('#000', 0.02),
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {format(parseLocalDate(date), 'MMM d, yyyy')} ({times.start_time} - {times.end_time})
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveClosure(date)}
                                        sx={{
                                          color: 'text.secondary',
                                          '&:hover': {
                                            color: 'error.main'
                                          }
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Paper>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Box>
                    </Collapse>
                  </Grid>

                  {/* Retry Config Toggle */}
                  <Grid item xs={12}>
                    <Tooltip title="Enable retry configuration to automatically retry failed or unanswered calls. Note: Calls that hit voicemail will not be retried since voicemail technically counts as being answered. When enabled, you can set the maximum number of retries and the time window for retry attempts." arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useRetryConfig}
                            onChange={(e) => setUseRetryConfig(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Use Retry Configuration
                            <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 16 }} />
                          </Box>
                        }
                      />
                    </Tooltip>
                  </Grid>

                  {/* Retry Config Section */}
                  <Grid item xs={12}>
                    <Collapse in={useRetryConfig}>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Max Retries"
                                type="number"
                                value={retryConfig.max_retries}
                                onChange={(e) => setRetryConfig(prev => ({
                                  ...prev,
                                  max_retries: parseInt(e.target.value) || 0
                                }))}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                    <Tooltip title="Maximum number of retry attempts if a call fails or goes unanswered" arrow>
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                    </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Retry Time Limit (minutes)"
                                type="number"
                                value={retryConfig.retry_time_limit_minutes}
                                onChange={(e) => setRetryConfig(prev => ({
                                  ...prev,
                                  retry_time_limit_minutes: parseInt(e.target.value) || 0
                                }))}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                    <Tooltip title="Maximum time window (in minutes) to attempt retries after the initial call" arrow>
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                    </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </Grid>
                </Grid>
              </Box>
            )}

            {(activeStep === 1 || (isEditMode && activeStep === 0)) && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {isEditMode ? 'Manage Contact Lists' : 'Upload Contact List'}
                </Typography>

                {/* Existing Lists (shown when lists exist, in both edit and create modes) */}
                {contactLists.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Existing Contact Lists</Typography>
                    <Grid container spacing={2}>
                      {contactLists.map((list) => (
                        <Grid item xs={12} sm={6} md={4} key={list.id}>
                          <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {list.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: alpha('#000', 0.6) }}>
                                {list.contact_count || 0} contacts
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteList(list.id.toString())}
                              disabled={isLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Contacts Table (shown when contacts exist, in both edit and create modes) */}
                {contacts.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      All Contacts ({contacts.length})
                    </Typography>
                    <Box sx={{ width: '100%', overflowX: 'auto', maxWidth: '100%' }}>
                      <TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
                        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '12%', px: 1 }}><strong>Contact List</strong></TableCell>
                              <TableCell sx={{ width: '14%', px: 1 }}><strong>Phone</strong></TableCell>
                              <TableCell sx={{ width: '10%', px: 1 }}><strong>Name</strong></TableCell>
                              <TableCell sx={{ width: '16%', px: 1 }}><strong>Email</strong></TableCell>
                              <TableCell sx={{ width: '20%', px: 1 }}><strong>Custom Fields</strong></TableCell>
                              <TableCell sx={{ width: '20%', px: 1 }}><strong>Prompt Variables</strong></TableCell>
                              {!isCampaignLaunched && <TableCell sx={{ width: '8%', px: 1 }}><strong>Actions</strong></TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contacts.map((contact: any) => {
                              const customFieldsKeys = contact.custom_fields ? Object.keys(contact.custom_fields) : [];
                              const promptVarsKeys = Object.keys(contact.prompt_variables || {});
                              
                              return (
                                <TableRow key={contact.id}>
                                  <TableCell sx={{ px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {contact.contact_list_name || '-'}
                                  </TableCell>
                                  <TableCell sx={{ px: 1, wordBreak: 'break-word', lineHeight: 1.4 }}>
                                    {contact.phone_number}
                                  </TableCell>
                                  <TableCell sx={{ px: 1, wordBreak: 'break-word', lineHeight: 1.4, overflowWrap: 'break-word' }}>
                                    {contact.first_name || contact.last_name
                                      ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                                      : '-'}
                                  </TableCell>
                                  <TableCell sx={{ px: 1, wordBreak: 'break-all', lineHeight: 1.4, overflowWrap: 'break-word' }}>
                                    {contact.email || '-'}
                                  </TableCell>
                                  <TableCell sx={{ px: 1 }}>
                                    {customFieldsKeys.length > 0 ? (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: '60px', overflowY: 'auto' }}>
                                        {customFieldsKeys.map((key) => (
                                          <Chip
                                            key={key}
                                            label={`${key}: ${String(contact.custom_fields[key]).substring(0, 20)}${String(contact.custom_fields[key]).length > 20 ? '...' : ''}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: '20px' }}
                                          />
                                        ))}
                                      </Box>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ px: 1 }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: '60px', overflowY: 'auto' }}>
                                      {promptVarsKeys.map((key) => (
                                        <Chip
                                          key={key}
                                          label={`${key}: ${String(contact.prompt_variables[key]).substring(0, 20)}${String(contact.prompt_variables[key]).length > 20 ? '...' : ''}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: '20px' }}
                                        />
                                      ))}
                                    </Box>
                                  </TableCell>
                                  {!isCampaignLaunched && (
                                    <TableCell sx={{ px: 1 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditContact(contact)}
                                        disabled={isLoading}
                                        sx={{ padding: '4px' }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteContact(contact.id)}
                                        disabled={isLoading}
                                        sx={{ padding: '4px' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                )}

                {/* Edit Contact Dialog */}
                <Dialog open={!!editingContact} onClose={() => setEditingContact(null)} maxWidth="sm" fullWidth>
                  <DialogTitle>Edit Contact</DialogTitle>
                  <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <TextField
                          label="Phone Number"
                          fullWidth
                          required
                          value={editFormData.phone_number || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="First Name"
                          fullWidth
                          value={editFormData.first_name || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last Name"
                          fullWidth
                          value={editFormData.last_name || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Email"
                          fullWidth
                          type="email"
                          value={editFormData.email || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Custom Fields (JSON format)</Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={JSON.stringify(editFormData.custom_fields || {}, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setEditFormData({ ...editFormData, custom_fields: parsed });
                            } catch {
                              // Invalid JSON, keep as is
                            }
                          }}
                          placeholder='{"key": "value"}'
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setEditingContact(null)} startIcon={<CancelIcon />}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveContact} variant="contained" startIcon={<SaveIcon />} disabled={isLoading || !editFormData.phone_number}>
                      Save
                    </Button>
                  </DialogActions>
                </Dialog>
                
                <Box sx={{ mb: 3, p: 2, bgcolor: alpha('#1976d2', 0.08), borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>CSV Format Requirements:</Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    <strong>Required column:</strong> <code>phone_number</code>
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    <strong>Phone number format:</strong> US numbers accepted in various formats:
                    <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                      <li>10 digits: <code>1234567890</code></li>
                      <li>With formatting: <code>(123) 456-7890</code> or <code>123-456-7890</code></li>
                      <li>E.164 format: <code>+11234567890</code></li>
                    </ul>
                    Numbers will be automatically normalized to E.164 format (+1XXXXXXXXXX).
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    <strong>Optional columns:</strong> <code>first_name</code>, <code>last_name</code>, <code>email</code>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Any additional columns will be saved as custom fields.
                  </Typography>
                  <Button
                    component="a"
                    href="/sample-contact-list.csv"
                    download="sample-contact-list.csv"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Download Sample CSV
                  </Button>
                </Box>

                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    border: '2px dashed', 
                    borderColor: isDragging ? 'primary.main' : 'grey.400',
                    borderRadius: 2, 
                    p: 4,
                    bgcolor: isDragging ? alpha('#1976d2', 0.05) : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer'
                  }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                    <UploadFileIcon sx={{ fontSize: 48, color: isDragging ? 'primary.main' : 'grey.500' }} />
                    <Typography sx={{ mt: 2, color: isDragging ? 'primary.main' : 'inherit' }}>
                      {isDragging ? 'Drop the CSV file here' : 'Drag and drop a CSV file here, or click to select a file.'}
                    </Typography>
                    <Button component="label" variant="contained" sx={{ mt: 2 }}>
                        Select File
                        <input type="file" accept=".csv" hidden onChange={handleFileSelect} />
                    </Button>
                    {csvFile && (
                      <Box sx={{ mt: 2, width: '100%', maxWidth: 400 }}>
                        <TextField
                          label="List Name (optional)"
                          fullWidth
                          size="small"
                          value={listName}
                          onChange={(e) => setListName(e.target.value)}
                          placeholder={csvFile.name}
                          sx={{ mb: 1 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Optional name for this contact list. If not provided, the filename will be used." arrow>
                                  <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Typography variant="body2" sx={{ color: alpha('#000', 0.6) }}>
                          Selected file: {csvFile.name}
                        </Typography>
                      </Box>
                    )}
                    {csvFile && campaignId && (
                      <Button
                        variant="contained"
                        onClick={handleUploadList}
                        disabled={isLoading}
                        sx={{ mt: 2 }}
                      >
                        {isLoading ? 'Uploading...' : 'Upload List'}
                      </Button>
                    )}
                    {importReport && (
                      <Box sx={{ width: '100%', textAlign: 'left' }}>
                        <ImportResult report={importReport} />
                      </Box>
                    )}
                </Box>
              </Box>
            )}

            {activeStep === 2 && !isEditMode && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Call Configuration
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  Configure voicemail handling, greeting, and instructions for your campaign calls. These settings will be applied to all calls in this campaign.
                </Typography>

                <Grid container spacing={3}>
                  {/* Voicemail Handling Toggle */}
                  <Grid item xs={12}>
                    <Tooltip title="Enable voicemail detection and handling. When enabled, you can configure the agent to leave a static message or hang up when voicemail is detected." arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useVoicemailHandling}
                            onChange={(e) => setUseVoicemailHandling(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Voicemail Handling
                            <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 16 }} />
                          </Box>
                        }
                      />
                    </Tooltip>
                  </Grid>

                  {/* Voicemail Handling Section */}
                  <Grid item xs={12}>
                    <Collapse in={useVoicemailHandling}>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Tooltip title="How the agent should handle voicemail detection. Static message supports template variables like {caller_name} that will be replaced with actual values. Hangup will immediately end the call." arrow>
                              <FormControl fullWidth>
                                <InputLabel>Voicemail Action</InputLabel>
                                <Select
                                  value={voicemailOption.type}
                                  label="Voicemail Action"
                                  onChange={(e) => setVoicemailOption(prev => ({
                                    ...prev,
                                    type: e.target.value as 'static_text' | 'hangup'
                                  }))}
                                >
                                  <MenuItem value="static_text">Leave Static Message</MenuItem>
                                  <MenuItem value="hangup">Hang Up</MenuItem>
                                </Select>
                              </FormControl>
                            </Tooltip>
                          </Grid>
                          {voicemailOption.type === 'static_text' && (
                            <>
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                                  Available Variables (click to insert):
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                  {availablePromptVariables.map((variable) => (
                                    <Chip
                                      key={variable.value}
                                      label={variable.display}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        const currentText = voicemailOption.text || '';
                                        setVoicemailOption(prev => ({
                                          ...prev,
                                          text: currentText + variable.value
                                        }));
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  ))}
                                </Stack>
                              </Grid>
                              <Grid item xs={12}>
                                <Tooltip 
                                  title="Template message with variables. Use {variableName} format. Variables will be replaced with actual values from each contact when the call is scheduled."
                                  arrow
                                >
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Voicemail Message Template"
                                    value={voicemailOption.text || ''}
                                    onChange={(e) => setVoicemailOption(prev => ({
                                      ...prev,
                                      text: e.target.value
                                    }))}
                                    placeholder="Hi {caller_name}, please give us a callback tomorrow at 10am. You can reach us at {phone_number}."
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Tooltip>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Grid>

                  {/* Campaign Greeting and Instructions */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                      Campaign Greeting and Instructions
                    </Typography>
                    <Tooltip 
                      title="Set the greeting and instructions for all calls in this campaign. Templates will be compiled per contact using each contact's data when the campaign is launched."
                      arrow
                    >
                      <InfoIcon sx={{ ml: 1, fontSize: 18, color: alpha('#000', 0.4), verticalAlign: 'middle', mb: 1 }} />
                    </Tooltip>
                    
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                            Greeting Variables (click to insert):
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                            {availablePromptVariables.map((variable) => (
                              <Chip
                                key={variable.value}
                                label={variable.display}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const currentGreeting = campaignGreeting || '';
                                  setCampaignGreeting(currentGreeting + variable.value);
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Tooltip 
                            title="The greeting for all campaign calls. Use {variableName} format for template variables. Will be compiled per contact using contact data when campaign is launched."
                            arrow
                          >
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Greeting"
                              value={campaignGreeting}
                              onChange={(e) => setCampaignGreeting(e.target.value)}
                              placeholder="Hi {caller_name}, this is a call from..."
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                            Instructions Variables (click to insert):
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                            {availablePromptVariables.map((variable) => (
                              <Chip
                                key={variable.value}
                                label={variable.display}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const currentInstructions = campaignInstructions || '';
                                  setCampaignInstructions(currentInstructions + variable.value);
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Tooltip 
                            title="Additional instructions for the agent for all campaign calls. Use {variableName} format for template variables. Will be compiled per contact using contact data when campaign is launched."
                            arrow
                          >
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label="Campaign Instructions"
                              value={campaignInstructions}
                              onChange={(e) => setCampaignInstructions(e.target.value)}
                              placeholder="Remember to mention {appointment_date} when speaking with {caller_name}..."
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={handleBack} disabled={activeStep === 0 || isLoading}>
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === 0 && !isEditMode && (
            <Tooltip 
              title={step0ValidationMessage || ''} 
              arrow 
              disableHoverListener={isStep0Valid || isLoading}
              disableFocusListener={isStep0Valid || isLoading}
              disableTouchListener={isStep0Valid || isLoading}
            >
              <span>
                <Button 
                  variant="contained" 
                  onClick={handleCreateCampaign} 
                  disabled={!isStep0Valid || isLoading}
                >
                  Next
                </Button>
              </span>
            </Tooltip>
          )}
          {activeStep === 0 && isEditMode && (
            <>
              <Tooltip 
                title={step0ValidationMessage || ''} 
                arrow 
                disableHoverListener={isStep0Valid || isLoading}
                disableFocusListener={isStep0Valid || isLoading}
                disableTouchListener={isStep0Valid || isLoading}
              >
                <span>
                  <Button 
                    variant="contained" 
                    onClick={handleCreateCampaign} 
                    disabled={!isStep0Valid || isLoading}
                  >
                    Save Changes
                  </Button>
                </span>
              </Tooltip>
              {!isCampaignLaunched && contacts.length > 0 && (
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={handleLaunchCampaign} 
                  disabled={isLoading}
                >
                  Launch Campaign
                </Button>
              )}
            </>
          )}
          {activeStep === 1 && !isEditMode && (
            <>
              <Button variant="contained" onClick={handleUploadList} disabled={!isStep1Valid || isLoading}>
                Upload List
              </Button>
              {contactLists.length > 0 && (
                <Button 
                  variant="contained" 
                  onClick={() => setActiveStep(2)}
                  disabled={isLoading}
                >
                  Next
                </Button>
              )}
            </>
          )}
          {activeStep === 2 && !isEditMode && (
            <>
              <Button 
                variant="contained" 
                onClick={handleSaveCallConfiguration}
                disabled={isLoading}
              >
                Save Configuration
              </Button>
              {contactLists.length > 0 && (
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={handleLaunchCampaign} 
                  disabled={isLoading}
                >
              Launch Campaign
            </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {campaignId && (
        <LaunchConfirmDialog
          open={confirmOpen}
          campaignId={campaignId}
          onLaunched={handleLaunched}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </Box>
  );
};

export default CreateCampaign;
